/**
 * @file Vue-related helpers
 */
import { hyphenatedCurrentLocale } from 'bits/current_locale'
import { DOMContentLoadedPromise } from 'bits/dom'
import environment from 'bits/environment'
import * as errorTracker from 'bits/error_tracker'
import fixMobileViewport from 'bits/fix_mobile_viewport'
import { isDebugMode } from 'bits/flip'
import window from 'bits/global'
import { np__, n__, N__, p__, __ } from 'bits/intl'
import { reload } from 'bits/location'
import { isArray, isEqual, isObject } from 'lodash'
import { createPinia, Pinia as PiniaStore, PiniaVuePlugin } from 'pinia'
import PiniaLogger, { shouldLog } from 'pinia/plugin/logger'
import { Id } from 'types'
import Vue, { Component, CreateElement, del as vDel, set as vSet, VNode } from 'vue'
import {
  Component as VuePropertyDecoratorComponent,
  Emit as VuePropertyDecoratorEmit,
  Mixins as VuePropertyDecoratorMixins,
  Model as VuePropertyDecoratorModel,
  Prop as VuePropertyDecoratorProp,
  Vue as VuePropertyDecoratorVue,
  Watch as VuePropertyDecoratorWatch,
} from 'vue-property-decorator'
import Vuex, { Store as VuexStore, StoreOptions as VuexStoreOptions } from 'vuex'
import {
  Action as VuexClassAction,
  Getter as VuexClassGetter,
  namespace as VuexClassNamespace,
  State as VuexClassState,
} from 'vuex-class'

// Indicates errors that can be potentially fixed by reloading the page.
export class PageReloadableError extends Error {}

declare module 'vue/types/vue' {
  interface Vue {
    __: (phrase: string, args?: object) => string
    n__: (singularPhrase: string, pluralPhrase: string, num: number, args: object) => string
    p__: (context: string, phrase: string, args?: object) => string
    np__: (context: string, singularPhrase: string, pluralPhrase: string, num: number, args: object) => string
    N__: (phrase: string) => string
  }
}

interface VueAppOptions {
  // Element to mount on
  el: string

  // Component config
  rootComponent: Component
  rootComponentProps?: () => Record<string, any>
  plugins?: any[]
  created?: () => void // initialize store
  mounted?: () => void
  beforeMountPromises?: Array<Promise<any>>
  otherVueOptions?: Record<string, any>

  // Store
  store?: VuexStore<any>
  storeOptions?: VuexStoreOptions<any>
  initializeStore?: (store: VuexStore<any>) => void
  usePinia?: boolean

  // Error-tracking
  environment?: string
  locale?: string
  release?: string

  // Others
  disableAccessibilityChecks?: boolean
}

// Temporary no-op function. Will be replaced when we move to Vue3.
// Context: https://v3-migration.vuejs.org/breaking-changes/async-components.html
export const defineAsyncComponent = (component: any): any => component

export async function setupVueApp(options: VueAppOptions): Promise<Vue | undefined> {
  require('styles/tailwind.css')
  const app = Vue

  setupErrorTracking(app, options)
  setupIntl(app)
  setupPlugins(app, options)

  const vueInstance = await Promise.all([DOMContentLoadedPromise, ...(options.beforeMountPromises ?? [])])
    .then(() => {
      // Mobile browsers handle 100% height in ways we don't like. Fix that.
      fixMobileViewport()

      // Evaluate props only after DOMContentLoaded to ensure scripts that set globals on `window` have run.
      // Stores might also be initialized with data from `window` or `ww`, so initialize it only after DOMContentLoaded.
      const { vuexStore, piniaStore } = setupStores(app, options)
      const props = typeof options.rootComponentProps === 'function' ? options.rootComponentProps() : undefined
      const vueInstanceOptions = {
        el: options.el,
        store: vuexStore,
        pinia: piniaStore,
        created: options.created,
        mounted: options.mounted,
        render: (h: CreateElement): VNode => h(options.rootComponent, { props }),
        ...options.otherVueOptions,
      }
      const vueApp = new Vue(vueInstanceOptions)
      window.app = vueApp
      return vueApp
    })
    .catch((e) => {
      if (e instanceof PageReloadableError) {
        reload()
      } else {
        errorTracker.captureException(e)
      }
      return undefined
    })

  // Allow dev tools to be turned on in production
  if (process.env.NODE_ENV === 'production' && isDebugMode) {
    Vue.config.devtools = true
    Vue.config.performance = true
  }
  Vue.config.ignoredElements = ['trix-editor', 'lottie-player']

  return vueInstance
}

function setupErrorTracking(app, options: VueAppOptions): void {
  errorTracker.init({
    vue: app,
    environment: options.environment ?? environment,
    locale: options.locale ?? hyphenatedCurrentLocale,
    release: options.release ?? document?.documentElement?.dataset?.version,
  })
}

export function setupIntl(app): void {
  const global = app.prototype
  global.__ = __
  global.N__ = N__
  global.p__ = p__
  global.n__ = n__
  global.np__ = np__
}

function setupPlugins(app, options: VueAppOptions): void {
  // Use accessibility tools (in development) by default
  const disableAccessibilityChecks = options.disableAccessibilityChecks ?? false
  if (!disableAccessibilityChecks) void useVueAxePlugin(app)

  // Use other page-specific plugins
  const plugins = options.plugins
  if (!isArray(plugins) || plugins.length < 1) return
  plugins.forEach((plugin) => app.use(plugin))
}

async function useVueAxePlugin(app): Promise<void> {
  if (environment === 'production') return
  const VueAxePlugin = (await import('vue-axe')).default
  app.use(VueAxePlugin, {
    allowConsoleClears: false,
    clearConsoleOnUpdate: false,
  })
}

function setupVuexStore(app, options: VueAppOptions): VuexStore<any> | undefined {
  // Allow already-initialized store for now, to make porting Surface easier.
  let store = options.store

  if (isObject(options.storeOptions)) {
    app.use(Vuex)
    store = new Vuex.Store(options.storeOptions)
  }

  if (store !== undefined && typeof options.initializeStore === 'function') {
    options.initializeStore(store)
  }

  return store
}

function setupPiniaStore(app): PiniaStore | undefined {
  app.use(PiniaVuePlugin)
  const store = createPinia()

  if (shouldLog) {
    store.use(
      PiniaLogger({
        expanded: false,
        disabled: !shouldLog,
      }),
    )
  }

  return store
}

function setupStores(
  app,
  options: VueAppOptions,
): { vuexStore: VuexStore<any> | undefined; piniaStore: PiniaStore | undefined } {
  const usePinia = options.usePinia ?? false
  const vuexStore = setupVuexStore(app, options)
  const piniaStore = usePinia ? setupPiniaStore(app) : undefined

  return { vuexStore, piniaStore }
}

type IdArrayMap = Record<Id, Id[]>
function stableSync(target: IdArrayMap, source: IdArrayMap): IdArrayMap {
  new Set([...Object.keys(source), ...Object.keys(target)]).forEach(function (key) {
    const sourceIds = source[key]
    const destIds = target[key]
    if (isEqual(sourceIds, destIds)) return

    if (sourceIds != null) {
      Vue.set(target, key, [...sourceIds])
    } else {
      Vue.delete(target, key)
    }
  })
  return target
}

// Vue 3
//
// function vSet(object, key, value) {
//   object[key] = value
// }
//
// function vDel(object, key) {
//   delete object[key]
// }

export {
  vSet,
  vDel,
  stableSync,
  VuePropertyDecoratorProp as Prop,
  VuePropertyDecoratorWatch as Watch,
  VuePropertyDecoratorEmit as Emit,
  VuePropertyDecoratorComponent as Component,
  VuePropertyDecoratorMixins as Mixins,
  VuePropertyDecoratorModel as Model,
  VuePropertyDecoratorVue as Vue,
  VuexClassAction as Action,
  VuexClassGetter as Getter,
  VuexClassState as State,
  VuexClassNamespace as namespace,
}
